import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import MainLayout from '../../../../../components/layout/MainLayout';
import { getBackofficeSidebarItems } from '../../../shell/backofficeSidebar';
import { useAuth } from '../../../../../context/AuthContext';
import { PaginatedListLoading } from '../../../../../components/ui/PaginatedListLoading';
import {
  DEFAULT_DEPARTMENT_SLUG,
  setWorkspaceDepartmentScope,
  useDepartmentSlug,
} from '../../../../../utils/departmentRouting';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3001';

type PkgRow = {
  id: string;
  name: string;
  slug: string;
  departmentId?: string;
  sectionLabel?: string | null;
  headline?: string | null;
  blurb?: string | null;
  heroImageUrl?: string | null;
  priceUsd: number | string;
  isActive: boolean;
  sortOrder: number;
  items: Array<{
    productId: string;
    qty: number;
    unit: 'LB' | 'KG';
    product?: { id: string; name: string; baseUnit: string };
  }>;
};

type ProductOpt = {
  id: string;
  name: string;
  sku?: string;
  baseUnit: 'LB' | 'KG';
  isActive?: boolean;
  purchasePricePerUnit?: number | null;
  salePrice?: number | null;
  pricePerUnit?: number | null;
};

type ItemDraft = {
  productId: string;
  productQuery: string;
  qty: string;
  unit: 'LB' | 'KG';
};

const TRANSLATION_LOCALE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'fr', label: 'French (fr)' },
  { value: 'es', label: 'Spanish (es)' },
  { value: 'de', label: 'German (de)' },
];

const formKeys = [
  'name',
  'slug',
  'sectionLabel',
  'headline',
  'blurb',
  'heroImageUrl',
  'priceUsd',
  'isActive',
  'sortOrder',
] as const;

type FormKey = (typeof formKeys)[number];
const ALL_SCOPE_GROUP_PAGE_SIZE = 10;

export default function WorkspacePackagesPage() {
  const { authorizedFetch } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const departmentSlug = useDepartmentSlug() ?? DEFAULT_DEPARTMENT_SLUG;
  const [workspaceScope, setWorkspaceScope] = useState<string>(() => {
    if (typeof window === 'undefined') return '__all__';
    return window.localStorage.getItem('workspace_department_scope') || '__all__';
  });
  const [workspaceDepartments, setWorkspaceDepartments] = useState<
    Array<{ id: string; slug: string; name: string }>
  >([]);
  const [rows, setRows] = useState<PkgRow[]>([]);
  const [products, setProducts] = useState<ProductOpt[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [editing, setEditing] = useState<PkgRow | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<FormKey, string>>>({});
  const [itemErrors, setItemErrors] = useState<Record<number, string>>({});
  const [activationBlockedProducts, setActivationBlockedProducts] = useState<
    Array<{ id: string; name: string }>
  >([]);
  const [form, setForm] = useState({
    name: '',
    slug: '',
    sectionLabel: '',
    headline: '',
    blurb: '',
    heroImageUrl: '',
    priceUsd: '',
    isActive: 'true',
    sortOrder: '0',
  });
  const [itemsDraft, setItemsDraft] = useState<ItemDraft[]>([
    { productId: '', productQuery: '', qty: '1', unit: 'LB' },
  ]);
  const [openSuggestIdx, setOpenSuggestIdx] = useState<number | null>(null);
  const [translationLocale, setTranslationLocale] = useState('fr');
  const [translationForm, setTranslationForm] = useState({
    name: '',
    sectionLabel: '',
    headline: '',
    blurb: '',
  });
  const [translationLoading, setTranslationLoading] = useState(false);
  const [translationSaving, setTranslationSaving] = useState(false);
  const [translationSuccess, setTranslationSuccess] = useState<string | null>(null);
  const [allScopeGroupPages, setAllScopeGroupPages] = useState<Record<string, number>>({});

  const isAllScope = useMemo(() => {
    const scope = String(workspaceScope || '').trim().toLowerCase();
    return !scope || scope === '__all__';
  }, [workspaceScope]);
  const departmentsById = useMemo(
    () =>
      new Map(
        workspaceDepartments.map((dep) => [
          dep.id,
          { name: dep.name || dep.slug, slug: dep.slug },
        ]),
      ),
    [workspaceDepartments],
  );
  const groupedAllScopePackages = useMemo(() => {
    const groups = new Map<
      string,
      {
        departmentId: string;
        departmentName: string;
        departmentSlug: string;
        rows: PkgRow[];
      }
    >();
    rows.forEach((row) => {
      const depId = String(row.departmentId || '__unknown__');
      const dep = departmentsById.get(depId);
      const departmentName = dep?.name || 'Unknown department';
      const depSlug = dep?.slug || 'unknown';
      const existing = groups.get(depId);
      if (existing) {
        existing.rows.push(row);
      } else {
        groups.set(depId, {
          departmentId: depId,
          departmentName,
          departmentSlug: depSlug,
          rows: [row],
        });
      }
    });
    return Array.from(groups.values())
      .map((group) => ({
        ...group,
        rows: group.rows.slice().sort((a, b) => a.name.localeCompare(b.name)),
      }))
      .sort((a, b) => a.departmentName.localeCompare(b.departmentName));
  }, [departmentsById, rows]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const url = new URL(`${API_BASE}/catalog/packages/workspace/list`);
      if (!isAllScope) {
        url.searchParams.set('departmentSlug', departmentSlug);
      }
      const res = await authorizedFetch(url.toString());
      if (!res.ok) throw new Error('load');
      const data = (await res.json()) as PkgRow[];
      setRows(Array.isArray(data) ? data : []);
    } catch {
      setError(t('backoffice.packages_load_error', { defaultValue: 'Unable to load packages.' }));
    } finally {
      setLoading(false);
    }
  }, [authorizedFetch, departmentSlug, isAllScope, t]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const readScope = () =>
      (typeof window !== 'undefined'
        ? window.localStorage.getItem('workspace_department_scope')
        : null) || '__all__';
    const handleScopeChange = () => {
      setWorkspaceScope(readScope());
    };
    window.addEventListener(
      'workspace-department-scope:change',
      handleScopeChange as EventListener,
    );
    window.addEventListener('storage', handleScopeChange);
    return () => {
      window.removeEventListener(
        'workspace-department-scope:change',
        handleScopeChange as EventListener,
      );
      window.removeEventListener('storage', handleScopeChange);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authorizedFetch(`${API_BASE}/departments/workspace`);
        if (!res.ok || cancelled) return;
        const rowsData = (await res.json()) as Array<{ id?: string; slug?: string; name?: string }>;
        if (!cancelled) {
          setWorkspaceDepartments(
            (Array.isArray(rowsData) ? rowsData : [])
              .map((row) => ({
                id: String(row.id || '').trim(),
                slug: String(row.slug || '').trim().toLowerCase(),
                name: String(row.name || '').trim(),
              }))
              .filter((row) => Boolean(row.id && row.slug)),
          );
        }
      } catch {
        if (!cancelled) setWorkspaceDepartments([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authorizedFetch]);

  useEffect(() => {
    void (async () => {
      if (isAllScope) {
        setProducts([]);
        return;
      }
      try {
        const loadByStatus = async (status: 'ENABLED' | 'DISABLED') => {
          const url = new URL(`${API_BASE}/catalog/products`);
          url.searchParams.set('page', '1');
          // Backend max pageSize is 100 (validated by PaginationQueryDto).
          url.searchParams.set('pageSize', '100');
          url.searchParams.set('statusFilter', status);
          url.searchParams.set('departmentSlug', departmentSlug);
          const res = await authorizedFetch(url.toString());
          if (!res.ok) {
            const body = await res.json().catch(() => ({}));
            const msg = (body as { message?: string }).message || `Failed to load ${status.toLowerCase()} products`;
            throw new Error(msg);
          }
          const data = (await res.json()) as { items?: ProductOpt[] };
          return data.items || [];
        };

        const [enabled, disabled] = await Promise.all([
          loadByStatus('ENABLED'),
          loadByStatus('DISABLED'),
        ]);
        const merged = [...enabled, ...disabled];
        const byId = new Map<string, ProductOpt>();
        for (const p of merged) {
          byId.set(p.id, p);
        }
        setProducts(
          [...byId.values()].sort((a, b) => a.name.localeCompare(b.name)),
        );
      } catch {
        setError(
          t('backoffice.packages_products_load_error', {
            defaultValue: 'Unable to load products for package components.',
          }),
        );
      }
    })();
  }, [authorizedFetch, departmentSlug, isAllScope, t]);

  useEffect(() => {
    if (isAllScope && formOpen) {
      resetForm();
    }
  }, [isAllScope, formOpen]);

  useEffect(() => {
    if (!isAllScope) return;
    setAllScopeGroupPages((prev) => {
      const next: Record<string, number> = {};
      groupedAllScopePackages.forEach((group) => {
        const totalGroupPages = Math.max(
          1,
          Math.ceil(group.rows.length / ALL_SCOPE_GROUP_PAGE_SIZE),
        );
        const currentPage = Number(prev[group.departmentId] || 1);
        next[group.departmentId] = Math.min(
          Math.max(1, currentPage),
          totalGroupPages,
        );
      });
      return next;
    });
  }, [groupedAllScopePackages, isAllScope]);

  const clearFieldError = (key: FormKey) => {
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const suggestionsForRow = (query: string, rowIndex: number): ProductOpt[] => {
    const term = query.trim().toLowerCase();
    const chosenIds = new Set(
      itemsDraft
        .map((it, idx) => (idx === rowIndex ? '' : it.productId))
        .filter(Boolean),
    );
    const base = products.filter((p) => !chosenIds.has(p.id));
    if (!term) return base.slice(0, 8);
    return base
      .filter((p) => {
        const name = p.name.toLowerCase();
        const sku = (p.sku || '').toLowerCase();
        return name.includes(term) || sku.includes(term);
      })
      .slice(0, 8);
  };

  const chooseProduct = (rowIndex: number, product: ProductOpt) => {
    setItemsDraft((d) =>
      d.map((it, i) =>
        i === rowIndex
          ? {
              ...it,
              productId: product.id,
              productQuery: product.name,
              unit: product.baseUnit === 'KG' ? 'KG' : 'LB',
            }
          : it,
      ),
    );
    setOpenSuggestIdx(null);
    setItemErrors((prev) => {
      const next = { ...prev };
      delete next[rowIndex];
      return next;
    });
    setError(null);
  };

  const suggestedBundlePrice = React.useMemo(() => {
    return itemsDraft.reduce((sum, row) => {
      if (!row.productId) return sum;
      const p = products.find((x) => x.id === row.productId);
      if (!p) return sum;
      const unitPrice = Number(p.salePrice ?? p.pricePerUnit ?? 0);
      const qty = Number(row.qty);
      if (!Number.isFinite(unitPrice) || unitPrice < 0) return sum;
      if (!Number.isFinite(qty) || qty <= 0) return sum;
      return sum + unitPrice * qty;
    }, 0);
  }, [itemsDraft, products]);
  const suggestedPurchaseTotal = React.useMemo(() => {
    return itemsDraft.reduce((sum, row) => {
      if (!row.productId) return sum;
      const p = products.find((x) => x.id === row.productId);
      if (!p) return sum;
      const unitPrice = Number(p.purchasePricePerUnit ?? 0);
      const qty = Number(row.qty);
      if (!Number.isFinite(unitPrice) || unitPrice < 0) return sum;
      if (!Number.isFinite(qty) || qty <= 0) return sum;
      return sum + unitPrice * qty;
    }, 0);
  }, [itemsDraft, products]);

  const handleHeroImageChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setImageUploading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await authorizedFetch(`${API_BASE}/catalog/packages/workspace/image`, {
        method: 'POST',
        body: formData,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as { message?: string }).message || 'Unable to upload image.');
      }
      const data = (await res.json()) as { path: string };
      setForm((f) => ({ ...f, heroImageUrl: `${API_BASE}${data.path}` }));
      clearFieldError('heroImageUrl');
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : t('backoffice.packages_upload_error', { defaultValue: 'Unable to upload image.' }),
      );
    } finally {
      setImageUploading(false);
      event.target.value = '';
    }
  };

  const resetForm = () => {
    setEditing(null);
    setFormOpen(false);
    setFieldErrors({});
    setItemErrors({});
    setActivationBlockedProducts([]);
    setForm({
      name: '',
      slug: '',
      sectionLabel: '',
      headline: '',
      blurb: '',
      heroImageUrl: '',
      priceUsd: '',
      isActive: 'true',
      sortOrder: '0',
    });
    setItemsDraft([{ productId: '', productQuery: '', qty: '1', unit: 'LB' }]);
    setTranslationSuccess(null);
    setTranslationForm({ name: '', sectionLabel: '', headline: '', blurb: '' });
  };

  const startEdit = (row: PkgRow) => {
    setFormOpen(true);
    setFieldErrors({});
    setItemErrors({});
    setActivationBlockedProducts([]);
    setEditing(row);
    setForm({
      name: row.name,
      slug: row.slug,
      sectionLabel: row.sectionLabel || '',
      headline: row.headline || '',
      blurb: row.blurb || '',
      heroImageUrl: row.heroImageUrl || '',
      priceUsd: String(row.priceUsd),
      isActive: row.isActive ? 'true' : 'false',
      sortOrder: String(row.sortOrder ?? 0),
    });
    setItemsDraft(
      row.items?.length
        ? row.items.map((it) => ({
            productId: it.productId,
            productQuery:
              products.find((p) => p.id === it.productId)?.name ||
              it.product?.name ||
              '',
            qty: String(it.qty),
            unit: it.unit === 'KG' ? 'KG' : 'LB',
          }))
        : [{ productId: '', productQuery: '', qty: '1', unit: 'LB' }],
    );
    setTranslationSuccess(null);
    setTranslationForm({ name: '', sectionLabel: '', headline: '', blurb: '' });
  };

  useEffect(() => {
    let cancelled = false;
    if (!editing?.id) return () => { cancelled = true; };
    (async () => {
      setTranslationLoading(true);
      try {
        const url = new URL(`${API_BASE}/catalog/packages/workspace/${editing.id}/translations`);
        url.searchParams.set('locale', translationLocale);
        const res = await authorizedFetch(url.toString());
        if (!res.ok || cancelled) return;
        const data = (await res.json()) as {
          translations: {
            name?: string;
            sectionLabel?: string;
            headline?: string;
            blurb?: string;
          };
        };
        if (!cancelled) {
          setTranslationForm({
            name: data.translations.name || '',
            sectionLabel: data.translations.sectionLabel || '',
            headline: data.translations.headline || '',
            blurb: data.translations.blurb || '',
          });
        }
      } finally {
        if (!cancelled) setTranslationLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authorizedFetch, editing?.id, translationLocale]);

  const saveTranslations = async () => {
    if (!editing?.id) return;
    setTranslationSaving(true);
    setTranslationSuccess(null);
    try {
      const res = await authorizedFetch(
        `${API_BASE}/catalog/packages/workspace/${editing.id}/translations`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            locale: translationLocale,
            ...translationForm,
          }),
        },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const msg = (body as { message?: string }).message || 'Unable to save package translations.';
        throw new Error(msg);
      }
      setTranslationSuccess('Translation saved successfully.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unable to save package translations.');
    } finally {
      setTranslationSaving(false);
    }
  };

  /** Client-side: every form field and each component row must be filled before save. */
  const validateAll = (): boolean => {
    const fe: Partial<Record<FormKey, string>> = {};
    const msg = t('backoffice.packages_field_required', { defaultValue: 'This field is required.' });

    if (!form.name.trim()) fe.name = msg;
    if (!form.slug.trim()) fe.slug = msg;
    if (!form.sectionLabel.trim()) fe.sectionLabel = msg;
    if (!form.headline.trim()) fe.headline = msg;
    if (!form.blurb.trim()) fe.blurb = msg;
    if (!form.heroImageUrl.trim()) fe.heroImageUrl = msg;

    const hasPriceInput = form.priceUsd.trim() !== '';
    const price = Number(form.priceUsd);
    if (hasPriceInput && (!Number.isFinite(price) || price < 0)) {
      fe.priceUsd = t('backoffice.packages_price_required', {
        defaultValue: 'Enter a valid price (0 or greater).',
      });
    } else if (hasPriceInput && price > suggestedBundlePrice) {
      fe.priceUsd = t('backoffice.packages_price_must_not_exceed_live_total', {
        defaultValue: 'Bundle price must be less than or equal to Live items total.',
      });
    } else if (hasPriceInput && price < suggestedPurchaseTotal) {
      fe.priceUsd = t('backoffice.packages_price_must_cover_purchase_total', {
        defaultValue: 'Bundle price cannot be less than total purchase cost.',
      });
    }

    const sortRaw = form.sortOrder.trim();
    if (sortRaw === '' || !Number.isFinite(Number(sortRaw)) || Number(sortRaw) < 0 || !Number.isInteger(Number(sortRaw))) {
      fe.sortOrder = t('backoffice.packages_sort_required', {
        defaultValue: 'Enter a valid whole number (0 or greater).',
      });
    }

    const ie: Record<number, string> = {};
    const itemMsg = t('backoffice.packages_component_required', {
      defaultValue: 'Choose a product and a valid quantity.',
    });
    itemsDraft.forEach((row, idx) => {
      if (!row.productId.trim() || !products.some((p) => p.id === row.productId)) {
        ie[idx] = itemMsg;
        return;
      }
      const q = Number(row.qty);
      if (!Number.isFinite(q) || q <= 0) {
        ie[idx] = itemMsg;
      }
    });
    if (!itemsDraft.length) {
      ie[0] = t('backoffice.packages_need_items', { defaultValue: 'Add at least one product line.' });
    }

    setFieldErrors(fe);
    setItemErrors(ie);
    const ok = !Object.keys(fe).length && !Object.keys(ie).length;
    if (!ok) {
      setError(
        t('backoffice.packages_validation_summary', {
          defaultValue: 'Please fill in all required fields.',
        }),
      );
    }
    return ok;
  };

  const save = async () => {
    setError(null);
    setActivationBlockedProducts([]);
    if (!validateAll()) return;

    setSaving(true);
    try {
      const hasPriceInput = form.priceUsd.trim() !== '';
      const price = Number(form.priceUsd);
      const parsedItems = itemsDraft.map((it) => ({
        productId: it.productId.trim(),
        qty: Number(it.qty),
        unit: it.unit,
      }));
      const body = {
        name: form.name.trim(),
        slug: form.slug.trim(),
        departmentSlug,
        sectionLabel: form.sectionLabel.trim(),
        headline: form.headline.trim(),
        blurb: form.blurb.trim(),
        heroImageUrl: form.heroImageUrl.trim(),
        ...(hasPriceInput ? { priceUsd: price } : {}),
        isActive: form.isActive === 'true',
        sortOrder: Number(form.sortOrder),
        items: parsedItems,
      };
      const url = editing
        ? `${API_BASE}/catalog/packages/workspace/${editing.id}`
        : `${API_BASE}/catalog/packages/workspace`;
      const res = await authorizedFetch(url, {
        method: editing ? 'PATCH' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        const payload = b as {
          message?: string | string[];
          disabledProducts?: Array<{ id?: string; name?: string }>;
          fields?: string[];
        };
        const message = Array.isArray(payload.message)
          ? payload.message.join(' ')
          : payload.message;
        const disabledProducts = Array.isArray(payload.disabledProducts)
          ? payload.disabledProducts
              .filter((p): p is { id: string; name: string } => !!p?.id && !!p?.name)
          : [];
        if (disabledProducts.length > 0) {
          setActivationBlockedProducts(disabledProducts);
          if (Array.isArray(payload.fields) && payload.fields.includes('isActive')) {
            setFieldErrors((prev) => ({
              ...prev,
              isActive: t('backoffice.packages_activation_blocked_active', {
                defaultValue: 'Set Active to "No" for now, or enable the listed products first.',
              }),
            }));
          }
          const blockedIds = new Set(disabledProducts.map((p) => p.id));
          setItemErrors((prev) => {
            const next = { ...prev };
            itemsDraft.forEach((row, idx) => {
              if (blockedIds.has(row.productId)) {
                next[idx] = t('backoffice.packages_activation_blocked_item', {
                  defaultValue: 'This product is disabled. Enable it before activating this package.',
                });
              }
            });
            return next;
          });
        }
        throw new Error(message || 'save');
      }
      await load();
      resetForm();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'save');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: string) => {
    if (!window.confirm(t('backoffice.packages_delete_confirm', { defaultValue: 'Delete this package?' }))) return;
    setSaving(true);
    try {
      const res = await authorizedFetch(`${API_BASE}/catalog/packages/workspace/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('delete');
      await load();
      if (editing?.id === id) resetForm();
    } catch {
      setError(t('backoffice.packages_delete_error', { defaultValue: 'Could not delete.' }));
    } finally {
      setSaving(false);
    }
  };


  const inputClass = (key: FormKey) =>
    `mt-1 w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500/30 ${
      fieldErrors[key] ? 'border-red-400 bg-red-50/40' : 'border-slate-200'
    }`;

  const req = (
    <span className="text-red-600" aria-hidden>
      {' '}
      *
    </span>
  );

  return (
    <MainLayout withSidebar sidebarItems={getBackofficeSidebarItems(t)} activePath={location.pathname}>
      <main className="workspace-page">
        <section>
          <h1 className="workspace-page-title">
            {t('backoffice.packages_title', { defaultValue: 'Packages' })}
          </h1>
          <p className="workspace-page-subtitle text-slate-600">
            {t('backoffice.packages_subtitle', {
              defaultValue: 'Bundles of products sold at a fixed price.',
            })}
          </p>
        </section>

        <div className="space-y-6">
          {error ? (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</div>
          ) : null}

          {formOpen ? (
          <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 pb-4">
              <h2 className="text-base font-semibold text-slate-900">
                {editing ? t('backoffice.packages_edit', { defaultValue: 'Edit package' }) : t('backoffice.packages_create', { defaultValue: 'New package' })}
              </h2>
              <button
                type="button"
                onClick={resetForm}
                className="rounded-full bg-blue-600 px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700"
              >
                {t('common.cancel', { defaultValue: 'Cancel' })}
              </button>
            </div>

            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <label className="block text-xs font-medium text-slate-700">
                {t('backoffice.packages_name', { defaultValue: 'Package name' })}
                {req}
                <input
                  required
                  className={inputClass('name')}
                  value={form.name}
                  onChange={(e) => {
                    setForm((f) => ({ ...f, name: e.target.value }));
                    clearFieldError('name');
                    setError(null);
                  }}
                  placeholder={t('backoffice.packages_name_ph', { defaultValue: 'Period name' })}
                  autoComplete="off"
                />
                {fieldErrors.name ? <p className="mt-1 text-xs text-red-600">{fieldErrors.name}</p> : null}
              </label>

              <label className="block text-xs font-medium text-slate-700">
                {t('backoffice.packages_slug', { defaultValue: 'URL slug' })}
                {req}
                <input
                  required
                  className={inputClass('slug')}
                  value={form.slug}
                  onChange={(e) => {
                    setForm((f) => ({ ...f, slug: e.target.value }));
                    clearFieldError('slug');
                    setError(null);
                  }}
                  placeholder="my-bundle"
                  autoComplete="off"
                />
                {fieldErrors.slug ? <p className="mt-1 text-xs text-red-600">{fieldErrors.slug}</p> : null}
              </label>

              <label className="block text-xs font-medium text-slate-700">
                {t('backoffice.packages_section_label', { defaultValue: 'Section label' })}
                {req}
                <input
                  required
                  className={inputClass('sectionLabel')}
                  value={form.sectionLabel}
                  onChange={(e) => {
                    setForm((f) => ({ ...f, sectionLabel: e.target.value }));
                    clearFieldError('sectionLabel');
                    setError(null);
                  }}
                  placeholder={t('backoffice.packages_section_ph', { defaultValue: 'FEATURED' })}
                />
                {fieldErrors.sectionLabel ? (
                  <p className="mt-1 text-xs text-red-600">{fieldErrors.sectionLabel}</p>
                ) : null}
              </label>

              <label className="block text-xs font-medium text-slate-700">
                {t('backoffice.packages_sort', { defaultValue: 'Sort order' })}
                {req}
                <input
                  required
                  inputMode="numeric"
                  className={inputClass('sortOrder')}
                  value={form.sortOrder}
                  onChange={(e) => {
                    setForm((f) => ({ ...f, sortOrder: e.target.value }));
                    clearFieldError('sortOrder');
                    setError(null);
                  }}
                  placeholder="0"
                />
                {fieldErrors.sortOrder ? <p className="mt-1 text-xs text-red-600">{fieldErrors.sortOrder}</p> : null}
              </label>

              <label className="block text-xs font-medium text-slate-700">
                {t('backoffice.packages_headline', { defaultValue: 'Headline' })}
                {req}
                <input
                  required
                  className={inputClass('headline')}
                  value={form.headline}
                  onChange={(e) => {
                    setForm((f) => ({ ...f, headline: e.target.value }));
                    clearFieldError('headline');
                    setError(null);
                  }}
                />
                {fieldErrors.headline ? <p className="mt-1 text-xs text-red-600">{fieldErrors.headline}</p> : null}
              </label>

              <label className="block text-xs font-medium text-slate-700 sm:col-span-2">
                {t('backoffice.packages_hero', { defaultValue: 'Hero image' })}
                {req}
                <div className="mt-1 flex flex-wrap items-center gap-3">
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    onChange={(e) => {
                      void handleHeroImageChange(e);
                    }}
                    className="block w-full max-w-sm text-sm text-slate-700 file:mr-3 file:rounded-md file:border file:border-slate-300 file:bg-white file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-slate-700 hover:file:border-slate-400"
                  />
                  {imageUploading ? (
                    <span className="text-xs text-slate-500">
                      {t('backoffice.packages_uploading', { defaultValue: 'Uploading…' })}
                    </span>
                  ) : null}
                </div>
                <input
                  required
                  readOnly
                  className={`${inputClass('heroImageUrl')} mt-2`}
                  value={form.heroImageUrl}
                  placeholder={t('backoffice.packages_hero_uploaded_url', {
                    defaultValue: 'Uploaded image URL appears here',
                  })}
                />
                <p className="mt-1 text-[11px] text-slate-500">
                  {t('backoffice.packages_hero_hint', {
                    defaultValue: 'Upload an image file to set the package hero image.',
                  })}
                </p>
                {fieldErrors.heroImageUrl ? (
                  <p className="mt-1 text-xs text-red-600">{fieldErrors.heroImageUrl}</p>
                ) : null}
              </label>

              <label className="block text-xs font-medium text-slate-700 sm:col-span-2">
                {t('backoffice.packages_blurb', { defaultValue: 'Description' })}
                {req}
                <textarea
                  required
                  rows={3}
                  className={`${inputClass('blurb')} resize-y`}
                  value={form.blurb}
                  onChange={(e) => {
                    setForm((f) => ({ ...f, blurb: e.target.value }));
                    clearFieldError('blurb');
                    setError(null);
                  }}
                />
                {fieldErrors.blurb ? <p className="mt-1 text-xs text-red-600">{fieldErrors.blurb}</p> : null}
              </label>

              <label className="block text-xs font-medium text-slate-700">
                {t('backoffice.packages_active', { defaultValue: 'Active' })}
                {req}
                <select
                  required
                  className={`mt-1 w-full rounded-lg border px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500/30 ${
                    fieldErrors.isActive ? 'border-red-400 bg-red-50/40' : 'border-slate-200'
                  }`}
                  value={form.isActive}
                  onChange={(e) => {
                    setForm((f) => ({ ...f, isActive: e.target.value }));
                    clearFieldError('isActive');
                    setActivationBlockedProducts([]);
                    setError(null);
                  }}
                >
                  <option value="true">{t('common.yes', { defaultValue: 'Yes' })}</option>
                  <option value="false">{t('common.no', { defaultValue: 'No' })}</option>
                </select>
                {fieldErrors.isActive ? (
                  <p className="mt-1 text-xs text-red-600">{fieldErrors.isActive}</p>
                ) : null}
              </label>
            </div>

            {activationBlockedProducts.length > 0 ? (
              <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                <p className="font-semibold">
                  {t('backoffice.packages_activation_blocked_title', {
                    defaultValue: 'This package cannot be activated yet.',
                  })}
                </p>
                <p className="mt-1">
                  {t('backoffice.packages_activation_blocked_body', {
                    defaultValue:
                      'Some products in this package are disabled. Either keep this package inactive for now, or enable those products first.',
                  })}
                </p>
                <ul className="mt-2 list-disc pl-5">
                  {activationBlockedProducts.map((p) => (
                    <li key={p.id}>{p.name}</li>
                  ))}
                </ul>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    type="button"
                    className="rounded-md border border-amber-300 bg-white px-3 py-1.5 text-xs font-semibold text-amber-900 hover:bg-amber-100"
                    onClick={() => {
                      setForm((f) => ({ ...f, isActive: 'false' }));
                      clearFieldError('isActive');
                      setError(null);
                    }}
                  >
                    {t('backoffice.packages_keep_inactive', { defaultValue: 'Keep package inactive for now' })}
                  </button>
                  <button
                    type="button"
                    className="rounded-md border border-blue-300 bg-white px-3 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-50"
                    onClick={() => {
                      setWorkspaceDepartmentScope(departmentSlug);
                      navigate('/workspace/products');
                    }}
                  >
                    {t('backoffice.packages_go_enable_products', { defaultValue: 'Go enable products' })}
                  </button>
                </div>
              </div>
            ) : null}

            <div className="mt-8 border-t border-slate-100 pt-6">
              <p className="text-xs font-semibold text-slate-800">
                {t('backoffice.packages_items', { defaultValue: 'Components' })}
                {req}
              </p>
              <p className="mt-1 text-[11px] text-slate-500">
                {t('backoffice.packages_items_hint', {
                  defaultValue: 'Each line must include a product and a quantity greater than zero.',
                })}
              </p>
              <div className="mt-3 space-y-2">
                {itemsDraft.map((row, idx) => (
                  <div
                    key={idx}
                    className={`flex flex-wrap items-end gap-2 rounded-lg border p-3 ${
                      itemErrors[idx] ? 'border-red-300 bg-red-50/50' : 'border-slate-100 bg-slate-50/80'
                    }`}
                  >
                    <label className="relative min-w-[200px] flex-1 text-[11px] font-medium text-slate-600">
                      {t('backoffice.packages_product', { defaultValue: 'Product' })}
                      <input
                        required
                        className="mt-1 w-full rounded border border-slate-200 bg-white px-2 py-1.5 text-sm"
                        value={row.productQuery}
                        onFocus={() => setOpenSuggestIdx(idx)}
                        onBlur={() => {
                          window.setTimeout(() => {
                            setOpenSuggestIdx((cur) => (cur === idx ? null : cur));
                          }, 120);
                        }}
                        onChange={(e) => {
                          const query = e.target.value;
                          setItemsDraft((d) =>
                            d.map((it, i) =>
                              i === idx
                                ? { ...it, productId: '', productQuery: query }
                                : it,
                            ),
                          );
                          setOpenSuggestIdx(idx);
                          setItemErrors((prev) => {
                            const next = { ...prev };
                            delete next[idx];
                            return next;
                          });
                          setError(null);
                        }}
                        placeholder={t('backoffice.packages_search_product', {
                          defaultValue: 'Search product name',
                        })}
                      />
                      {openSuggestIdx === idx ? (
                        <div className="absolute left-0 right-0 z-20 mt-1 max-h-52 overflow-auto rounded-md border border-slate-200 bg-white shadow-lg">
                          {suggestionsForRow(row.productQuery, idx).length > 0 ? (
                            suggestionsForRow(row.productQuery, idx).map((p) => (
                              <button
                                key={p.id}
                                type="button"
                                className="block w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-100"
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  chooseProduct(idx, p);
                                }}
                              >
                                {p.name}
                                {p.sku ? ` (${p.sku})` : ''}
                                {p.isActive === false ? ' (disabled)' : ''}
                              </button>
                            ))
                          ) : (
                            <p className="px-3 py-2 text-xs text-slate-500">
                              {t('backoffice.packages_no_product_match', {
                                defaultValue: 'No matching products.',
                              })}
                            </p>
                          )}
                        </div>
                      ) : null}
                    </label>
                    <label className="w-24 text-[11px] font-medium text-slate-600">
                      {t('backoffice.packages_qty', { defaultValue: 'Qty' })}
                      <input
                        required
                        className="mt-1 w-full rounded border border-slate-200 bg-white px-2 py-1.5 text-sm"
                        value={row.qty}
                        onChange={(e) => {
                          setItemsDraft((d) => d.map((it, i) => (i === idx ? { ...it, qty: e.target.value } : it)));
                          setItemErrors((prev) => {
                            const next = { ...prev };
                            delete next[idx];
                            return next;
                          });
                          setError(null);
                        }}
                      />
                    </label>
                    <label className="w-20 text-[11px] font-medium text-slate-600">
                      {t('backoffice.packages_unit', { defaultValue: 'Unit' })}
                      <select
                        className="mt-1 w-full rounded border border-slate-200 bg-white px-2 py-1.5 text-sm"
                        value={row.unit}
                        onChange={(e) =>
                          setItemsDraft((d) =>
                            d.map((it, i) =>
                              i === idx ? { ...it, unit: e.target.value === 'KG' ? 'KG' : 'LB' } : it,
                            ),
                          )
                        }
                      >
                        <option value="LB">LB</option>
                        <option value="KG">KG</option>
                      </select>
                    </label>
                    <button
                      type="button"
                      className="rounded border border-slate-300 px-2 py-1 text-xs text-slate-700 disabled:opacity-40"
                      disabled={itemsDraft.length <= 1}
                      title={
                        itemsDraft.length <= 1
                          ? t('backoffice.packages_keep_one_line', { defaultValue: 'At least one component line is required.' })
                          : undefined
                      }
                      onClick={() =>
                        setItemsDraft((d) => {
                          if (d.length <= 1) return d;
                          setItemErrors((prev) => {
                            const next: Record<number, string> = {};
                            d.forEach((_, i) => {
                              if (i < idx && prev[i]) next[i] = prev[i];
                              if (i > idx && prev[i]) next[i - 1] = prev[i];
                            });
                            return next;
                          });
                          return d.filter((_, i) => i !== idx);
                        })
                      }
                    >
                      ×
                    </button>
                    {itemErrors[idx] ? <p className="w-full text-xs text-red-600">{itemErrors[idx]}</p> : null}
                  </div>
                ))}
                <button
                  type="button"
                  className="text-xs font-semibold text-blue-700 hover:underline"
                  onClick={() => {
                    setItemsDraft((d) => [...d, { productId: '', productQuery: '', qty: '1', unit: 'LB' }]);
                    setError(null);
                  }}
                >
                  + {t('backoffice.packages_add_line', { defaultValue: 'Add line' })}
                </button>
              </div>

              <div className="mt-5 grid gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4 sm:grid-cols-2">
                <div>
                  <p className="text-xs font-semibold text-slate-700">
                    {t('backoffice.packages_items_total_live', {
                      defaultValue: 'Live items total (sale price)',
                    })}
                  </p>
                  <p className="mt-1 text-lg font-semibold text-slate-900">
                    {Number.isFinite(suggestedBundlePrice)
                      ? `$${suggestedBundlePrice.toFixed(2)}`
                      : '$0.00'}
                  </p>
                  <p className="text-[11px] text-slate-500">
                    {t('backoffice.packages_items_total_hint', {
                      defaultValue:
                        'Calculated from selected item quantities and each product sale price (fallback: price/unit).',
                    })}
                  </p>
                </div>
                <label className="block text-xs font-medium text-slate-700">
                  {t('backoffice.packages_price', { defaultValue: 'Bundle price (USD)' })}
                  <div className="mt-1 flex items-center gap-2">
                    <input
                      inputMode="decimal"
                      className={inputClass('priceUsd')}
                      value={form.priceUsd}
                      onChange={(e) => {
                        setForm((f) => ({ ...f, priceUsd: e.target.value }));
                        clearFieldError('priceUsd');
                        setError(null);
                      }}
                      placeholder={t('backoffice.packages_price_optional_ph', { defaultValue: 'Optional - defaults to live total' })}
                    />
                    <button
                      type="button"
                      className="rounded-md border border-slate-300 bg-white px-2 py-1.5 text-[11px] font-semibold text-slate-700 hover:border-slate-400"
                      onClick={() => {
                        setForm((f) => ({ ...f, priceUsd: suggestedBundlePrice.toFixed(2) }));
                        clearFieldError('priceUsd');
                        setError(null);
                      }}
                    >
                      {t('backoffice.packages_use_total', { defaultValue: 'Use total' })}
                    </button>
                  </div>
                  {fieldErrors.priceUsd ? <p className="mt-1 text-xs text-red-600">{fieldErrors.priceUsd}</p> : null}
                  {!fieldErrors.priceUsd ? (
                    <p className="mt-1 text-[11px] text-slate-500">
                      Leave empty to use the live items total automatically.
                    </p>
                  ) : null}
                </label>
              </div>
            </div>

            {editing ? (
              <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-4">
                <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                    Dynamic content translations
                  </p>
                  <select
                    value={translationLocale}
                    onChange={(e) => {
                      setTranslationLocale(e.target.value);
                      setTranslationSuccess(null);
                    }}
                    className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs text-slate-800"
                  >
                    {TRANSLATION_LOCALE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
                {translationLoading ? (
                  <p className="text-xs text-slate-500">Loading translations...</p>
                ) : (
                  <div className="grid gap-3 sm:grid-cols-2">
                    <label className="text-xs font-medium text-slate-700">
                      Localized name
                      <input
                        className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                        value={translationForm.name}
                        onChange={(e) => {
                          setTranslationForm((p) => ({ ...p, name: e.target.value }));
                          setTranslationSuccess(null);
                        }}
                      />
                    </label>
                    <label className="text-xs font-medium text-slate-700">
                      Localized section label
                      <input
                        className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                        value={translationForm.sectionLabel}
                        onChange={(e) => {
                          setTranslationForm((p) => ({ ...p, sectionLabel: e.target.value }));
                          setTranslationSuccess(null);
                        }}
                      />
                    </label>
                    <label className="text-xs font-medium text-slate-700 sm:col-span-2">
                      Localized headline
                      <input
                        className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                        value={translationForm.headline}
                        onChange={(e) => {
                          setTranslationForm((p) => ({ ...p, headline: e.target.value }));
                          setTranslationSuccess(null);
                        }}
                      />
                    </label>
                    <label className="text-xs font-medium text-slate-700 sm:col-span-2">
                      Localized description
                      <textarea
                        rows={3}
                        className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                        value={translationForm.blurb}
                        onChange={(e) => {
                          setTranslationForm((p) => ({ ...p, blurb: e.target.value }));
                          setTranslationSuccess(null);
                        }}
                      />
                    </label>
                    <div className="sm:col-span-2">
                      <button
                        type="button"
                        onClick={saveTranslations}
                        disabled={translationSaving}
                        className="rounded-md border border-brand-500 bg-white px-4 py-2 text-xs font-semibold text-brand-700 hover:bg-brand-50 disabled:opacity-60"
                      >
                        {translationSaving ? 'Saving translation...' : 'Save translation'}
                      </button>
                      {translationSuccess ? (
                        <p className="mt-2 text-xs font-medium text-emerald-700">
                          {translationSuccess}
                        </p>
                      ) : null}
                    </div>
                  </div>
                )}
              </div>
            ) : null}

            <button
              type="button"
              disabled={saving}
              onClick={() => void save()}
              className="mt-8 w-full max-w-xs rounded-lg bg-blue-600 px-6 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:opacity-50 sm:w-auto"
            >
              {saving
                ? t('common.saving', { defaultValue: 'Saving…' })
                : editing
                  ? t('common.save', { defaultValue: 'Save' })
                  : t('common.create', { defaultValue: 'Create' })}
            </button>
          </div>
          ) : null}

          {!formOpen ? (
          <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
            <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-4 py-3">
              <h2 className="text-sm font-semibold text-slate-900">{t('backoffice.packages_list', { defaultValue: 'All packages' })}</h2>
              <button
                type="button"
                className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                disabled={isAllScope}
                title={
                  isAllScope
                    ? 'Select a specific department scope to create a package.'
                    : undefined
                }
                onClick={() => {
                  if (isAllScope) return;
                  setFormOpen(true);
                  setEditing(null);
                  setFieldErrors({});
                  setItemErrors({});
                  setActivationBlockedProducts([]);
                  setForm({
                    name: '',
                    slug: '',
                    sectionLabel: '',
                    headline: '',
                    blurb: '',
                    heroImageUrl: '',
                    priceUsd: '',
                    isActive: 'true',
                    sortOrder: '0',
                  });
                  setItemsDraft([{ productId: '', productQuery: '', qty: '1', unit: 'LB' }]);
                  setError(null);
                }}
              >
                {t('backoffice.packages_create_new', { defaultValue: 'Create new package' })}
              </button>
            </div>
            {isAllScope ? (
              <p className="px-4 pt-3 text-xs font-medium text-amber-700">
                Select a specific department scope to add a package.
              </p>
            ) : null}
            {loading ? (
              <PaginatedListLoading />
            ) : (
              !isAllScope ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-left text-sm">
                    <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                      <tr>
                        <th className="px-4 py-2">{t('backoffice.packages_name', { defaultValue: 'Name' })}</th>
                        <th className="px-4 py-2">{t('backoffice.packages_price', { defaultValue: 'Price' })}</th>
                        <th className="px-4 py-2">{t('backoffice.packages_active', { defaultValue: 'Active' })}</th>
                        <th className="px-4 py-2" />
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => (
                        <tr key={r.id} className="border-t border-slate-100">
                          <td className="px-4 py-2 font-medium text-slate-900">{r.name}</td>
                          <td className="px-4 py-2 tabular-nums">{Number(r.priceUsd).toFixed(2)}</td>
                          <td className="px-4 py-2">
                            <span
                              className={
                                r.isActive
                                  ? 'inline-flex rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700'
                                  : 'inline-flex rounded-full border border-rose-200 bg-rose-50 px-2.5 py-1 text-[11px] font-semibold text-rose-700'
                              }
                            >
                              {r.isActive
                                ? t('common.yes', { defaultValue: 'Yes' })
                                : t('common.no', { defaultValue: 'No' })}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-right">
                            <button type="button" className="mr-2 text-xs font-semibold text-blue-700" onClick={() => startEdit(r)}>
                              {t('common.edit', { defaultValue: 'Edit' })}
                            </button>
                            <button type="button" className="text-xs font-semibold text-red-700" onClick={() => void remove(r.id)}>
                              {t('common.delete', { defaultValue: 'Delete' })}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="space-y-4 px-4 pb-4">
                  {groupedAllScopePackages.map((group) => {
                    const groupPage = Math.max(1, Number(allScopeGroupPages[group.departmentId] || 1));
                    const groupTotalPages = Math.max(
                      1,
                      Math.ceil(group.rows.length / ALL_SCOPE_GROUP_PAGE_SIZE),
                    );
                    const groupPageSafe = Math.min(groupPage, groupTotalPages);
                    const groupSliceStart = (groupPageSafe - 1) * ALL_SCOPE_GROUP_PAGE_SIZE;
                    const groupRowsPaged = group.rows.slice(
                      groupSliceStart,
                      groupSliceStart + ALL_SCOPE_GROUP_PAGE_SIZE,
                    );
                    return (
                      <div key={group.departmentId} className="workspace-stat-card">
                        <div className="mb-3 flex flex-wrap items-center gap-2">
                          <span className="rounded-full bg-slate-900/5 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-700">
                            {group.departmentName}
                          </span>
                          <span className="text-[11px] text-slate-500">/{group.departmentSlug}</span>
                          <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-indigo-800">
                            {group.rows.length} package{group.rows.length > 1 ? 's' : ''}
                          </span>
                        </div>
                        <div className="overflow-x-auto">
                          <table className="min-w-full text-left text-sm">
                            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                              <tr>
                                <th className="px-4 py-2">{t('backoffice.packages_name', { defaultValue: 'Name' })}</th>
                                <th className="px-4 py-2">{t('backoffice.packages_price', { defaultValue: 'Price' })}</th>
                                <th className="px-4 py-2">{t('backoffice.packages_active', { defaultValue: 'Active' })}</th>
                                <th className="px-4 py-2" />
                              </tr>
                            </thead>
                            <tbody>
                              {groupRowsPaged.map((r) => (
                                <tr
                                  key={r.id}
                                  className="cursor-pointer border-t border-slate-100 hover:bg-indigo-50/40"
                                  onClick={() => {
                                    window.localStorage.setItem('workspace_department_scope', group.departmentSlug);
                                    window.dispatchEvent(
                                      new CustomEvent('workspace-department-scope:change', {
                                        detail: { scope: group.departmentSlug },
                                      }),
                                    );
                                  }}
                                >
                                  <td className="px-4 py-2 font-medium text-slate-900">{r.name}</td>
                                  <td className="px-4 py-2 tabular-nums">{Number(r.priceUsd).toFixed(2)}</td>
                                  <td className="px-4 py-2">
                                    <span
                                      className={
                                        r.isActive
                                          ? 'inline-flex rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700'
                                          : 'inline-flex rounded-full border border-rose-200 bg-rose-50 px-2.5 py-1 text-[11px] font-semibold text-rose-700'
                                      }
                                    >
                                      {r.isActive
                                        ? t('common.yes', { defaultValue: 'Yes' })
                                        : t('common.no', { defaultValue: 'No' })}
                                    </span>
                                  </td>
                                  <td className="px-4 py-2 text-right">
                                    <button
                                      type="button"
                                      className="mr-2 text-xs font-semibold text-blue-700"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        window.localStorage.setItem('workspace_department_scope', group.departmentSlug);
                                        window.dispatchEvent(
                                          new CustomEvent('workspace-department-scope:change', {
                                            detail: { scope: group.departmentSlug },
                                          }),
                                        );
                                      }}
                                    >
                                      {t('common.edit', { defaultValue: 'Edit' })}
                                    </button>
                                    <button
                                      type="button"
                                      className="text-xs font-semibold text-red-700"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        void remove(r.id);
                                      }}
                                    >
                                      {t('common.delete', { defaultValue: 'Delete' })}
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        {groupTotalPages > 1 ? (
                          <div className="mt-3 flex items-center justify-end gap-2 text-[11px] text-slate-600">
                            <button
                              type="button"
                              disabled={groupPageSafe <= 1}
                              onClick={() =>
                                setAllScopeGroupPages((prev) => ({
                                  ...prev,
                                  [group.departmentId]: Math.max(1, groupPageSafe - 1),
                                }))
                              }
                              className="rounded-full border border-slate-300 px-3 py-1 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              {t('backoffice.products_prev_page', 'Prev')}
                            </button>
                            <span>
                              {groupPageSafe} / {groupTotalPages}
                            </span>
                            <button
                              type="button"
                              disabled={groupPageSafe >= groupTotalPages}
                              onClick={() =>
                                setAllScopeGroupPages((prev) => ({
                                  ...prev,
                                  [group.departmentId]: Math.min(groupTotalPages, groupPageSafe + 1),
                                }))
                              }
                              className="rounded-full border border-slate-300 px-3 py-1 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              {t('backoffice.products_next_page', 'Next')}
                            </button>
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              )
            )}
          </div>
          ) : null}
        </div>
      </main>
    </MainLayout>
  );
}
